import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TreePine, Rocket, DollarSign, Target, BarChart3, MousePointerClick, Users, Eye, ShoppingCart, TrendingUp } from 'lucide-react';
export function AdsSlide() {
  const [view, setView] = useState<'90day' | '12mo'>('90day');
  // === ORGANIC (Grow Plan) ===
  // $447/mo flat. No ad spend. SEO compounds.
  // Revenue ramp: near-zero months 1-7, kicks in month 8+
  // $0 cost per organic acquisition once rankings hit
  const organicMonthlyCost = 447;
  const organicMonthlyRevenue = [0, 0, 0, 200, 400, 800, 1500, 3000, 5000, 7000, 9000, 11000];
  // === PAID (Ad-heavy approach) ===
  // $747/mo management + ~$1,500/mo ad spend = $2,247/mo
  // Immediate results but you pay per click forever
  // Revenue is consistent but cost never drops
  const paidMonthlyCost = 2247; // $747 mgmt + $1,500 ad spend
  const paidMonthlyRevenue = [2500, 3500, 4500, 4500, 4500, 4500, 4500, 4500, 4500, 4500, 4500, 4500];
  // 90-day calculations
  const organic90Cost = organicMonthlyCost * 3;
  const organic90Rev = organicMonthlyRevenue.slice(0, 3).reduce((a, b) => a + b, 0);
  const organic90ROI = organic90Rev > 0 ? (organic90Rev - organic90Cost) / organic90Cost * 100 : -100;
  const paid90Cost = paidMonthlyCost * 3;
  const paid90Rev = paidMonthlyRevenue.slice(0, 3).reduce((a, b) => a + b, 0);
  const paid90ROI = (paid90Rev - paid90Cost) / paid90Cost * 100;
  // 12-month calculations
  const organic12Cost = organicMonthlyCost * 12;
  const organic12Rev = organicMonthlyRevenue.reduce((a, b) => a + b, 0);
  const organic12ROI = (organic12Rev - organic12Cost) / organic12Cost * 100;
  const paid12Cost = paidMonthlyCost * 12;
  const paid12Rev = paidMonthlyRevenue.reduce((a, b) => a + b, 0);
  const paid12ROI = (paid12Rev - paid12Cost) / paid12Cost * 100;
  // 12-month profit
  const organic12Profit = organic12Rev - organic12Cost;
  const paid12Profit = paid12Rev - paid12Cost;
  // Chart data: max height reference
  const allRevValues = [...organicMonthlyRevenue, ...paidMonthlyRevenue];
  const maxRev = Math.max(...allRevValues, 1);
  return <section id="ads" className="min-h-screen bg-dark py-24 px-4 flex flex-col justify-center" data-id="element-29" data-mp="27bcd1nhmm9ib">
      <div className="max-w-7xl mx-auto w-full" data-id="element-30" data-mp="1xfj5yjmof87x">
        <div className="text-center mb-16" data-id="element-31" data-mp="1xgap2uz37dhb">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-32" data-mp="2l53ncy36lxf3">
            Paid vs Organic Traffic
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-33" data-mp="2gc0r2i70s9v7">
            Most agencies pick one. We build both.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-34" data-mp="1761tu9edul37">
          {/* Organic Card */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-dark-card border border-green-500/30 rounded-2xl p-8 relative overflow-hidden group hover:bg-green-900/5 transition-colors" data-id="element-35" data-mp="125w1ss47awkl">
            <div className="flex items-center mb-6" data-id="element-36" data-mp="jf4urx7ori9d">
              <div className="p-3 bg-green-500/20 rounded-lg mr-4" data-id="element-37" data-mp="1zu61v1kffa92">
                <TreePine className="text-green-500" size={32} data-id="element-38" data-mp="1zscx5rtdj03p" />
              </div>
              <div data-id="element-39" data-mp="1uul2wr59b1wc">
                <h3 className="text-2xl font-bold text-white" data-id="element-40" data-mp="1ux78wmci19cg">
                  Organic Growth
                </h3>
                <span className="text-green-400 text-sm font-medium" data-id="element-41" data-mp="9d2iva3kw3vl">
                  Grow Plan — $447/mo
                </span>
              </div>
            </div>
            <ul className="space-y-4 text-text-muted" data-id="element-42" data-mp="ddcmwh7t8k7t">
              <li className="flex items-center" data-id="element-43" data-mp="iea5guwqfg9u">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3" data-id="element-44" data-mp="if1x5un7ktuu"></span>
                Built once, compounds over time
              </li>
              <li className="flex items-center" data-id="element-45" data-mp="sbeqgreud5p2">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3" data-id="element-46" data-mp="sa3rd4wccm4a"></span>
                $0 cost per click after rankings hit
              </li>
              <li className="flex items-center" data-id="element-47" data-mp="114rwmv1x23t3">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3" data-id="element-48" data-mp="113gtmxgap031"></span>
                Slow start — real results by month 8
              </li>
              <li className="flex items-center" data-id="element-49" data-mp="1b1mr92v2jk23">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3" data-id="element-50" data-mp="1b0bo959g6gc1"></span>
                Traffic grows even if you pause spending
              </li>
            </ul>
          </motion.div>

          {/* Paid Card */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-dark-card border border-orange-500/30 rounded-2xl p-8 relative overflow-hidden group hover:bg-orange-900/5 transition-colors" data-id="element-51" data-mp="1b86xj8v9ee9w">
            <div className="flex items-center mb-6" data-id="element-52" data-mp="1u69fj3pe3ay7">
              <div className="p-3 bg-orange-500/20 rounded-lg mr-4" data-id="element-53" data-mp="1u4yaz1o4dz61">
                <Rocket className="text-orange-500" size={32} data-id="element-54" data-mp="1q4o7bvnsvkuc" />
              </div>
              <div data-id="element-55" data-mp="2modojj1f2vt6">
                <h3 className="text-2xl font-bold text-white" data-id="element-56" data-mp="2mowvy5tfy1sy">
                  Paid Acceleration
                </h3>
                <span className="text-orange-400 text-sm font-medium" data-id="element-57" data-mp="2huixfujhfek2">
                  ~$2,247/mo (mgmt + ad spend)
                </span>
              </div>
            </div>
            <ul className="space-y-4 text-text-muted" data-id="element-58" data-mp="kxmz740l8cvl">
              <li className="flex items-center" data-id="element-59" data-mp="69jtzwudd57u">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-3" data-id="element-60" data-mp="6908651llwoj"></span>
                Immediate visibility from day 1
              </li>
              <li className="flex items-center" data-id="element-61" data-mp="3te9gm3775jmv">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-3" data-id="element-62" data-mp="3tceuilk6vlh7"></span>
                Pay per impression — costs never drop
              </li>
              <li className="flex items-center" data-id="element-63" data-mp="3jgbq191v7ar3">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-3" data-id="element-64" data-mp="3jhms94fnwag3"></span>
                $3-15 per click, every click
              </li>
              <li className="flex items-center" data-id="element-65" data-mp="1hooy95a5fbtk">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-3" data-id="element-66" data-mp="1hp8emhk5hs5h"></span>
                Traffic stops the moment you stop paying
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Budget Allocation Simulator */}
        <div className="bg-dark-card rounded-2xl p-8 border border-white/10 mb-16" data-id="element-67" data-mp="326zpzm9pma6v">
          <h3 className="text-2xl font-bold mb-2 text-center" data-id="element-68" data-mp="3gvdt7yb1g0ny">
            Budget Allocation Simulator
          </h3>
          <p className="text-text-muted text-center text-sm mb-8" data-id="element-69" data-mp="3c45jt29pkfas">
            Compare the real cost and ROI of organic vs paid — short-term and
            long-term
          </p>

          {/* Toggle: 90 days vs 12 months */}
          <div className="flex justify-center mb-8" data-id="element-70" data-mp="3ltp3ga58wd6p">
            <div className="bg-dark rounded-full p-1 flex gap-1 border border-white/10" data-id="element-71" data-mp="3olsi8sll6yd">
              <button onClick={() => setView('90day')} className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${view === '90day' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-text-muted hover:text-white'}`} data-id="element-72" data-mp="3uv5ac7wupx66">
                First 90 Days
              </button>
              <button onClick={() => setView('12mo')} className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${view === '12mo' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-text-muted hover:text-white'}`} data-id="element-73" data-mp="1emx367gn117i">
                12 Months
              </button>
            </div>
          </div>

          {/* Comparison Cards */}
          <motion.div key={view} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-74" data-mp="vq5ns947v68q">
            {/* Organic Column */}
            <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-6" data-id="element-75" data-mp="2h1dt0k3435ft">
              <div className="flex items-center gap-2 mb-4" data-id="element-76" data-mp="2m0qqrzvitwx6">
                <TreePine size={18} className="text-green-400" data-id="element-77" data-mp="2m3cyq0mbqefx" />
                <h4 className="font-bold text-green-400" data-id="element-78" data-mp="adn6ykrbow3s">
                  Organic (Grow Plan)
                </h4>
              </div>
              <div className="space-y-4" data-id="element-79" data-mp="5hg26vanxqnf">
                <div className="flex justify-between items-center" data-id="element-80" data-mp="nbrq226v0hl">
                  <span className="text-sm text-text-muted" data-id="element-81" data-mp="3ruy0g93rlt5b">Total Cost</span>
                  <span className="text-xl font-bold text-white" data-id="element-82" data-mp="3rstzo015xa99">
                    $
                    {(view === '90day' ? organic90Cost : organic12Cost).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-83" data-mp="3dwzofacx28sz">
                  <span className="text-sm text-text-muted" data-id="element-84" data-mp="38x3w48dl05t9">
                    Revenue Generated
                  </span>
                  <span className="text-xl font-bold text-white" data-id="element-85" data-mp="1c2qy1wgaf4pd">
                    $
                    {(view === '90day' ? organic90Rev : organic12Rev).toLocaleString()}
                  </span>
                </div>
                <div className="h-px bg-white/10" data-id="element-86" data-mp="2cmnzs1xcm4rj" />
                <div className="flex justify-between items-center" data-id="element-87" data-mp="2qmozvf5sh85y">
                  <span className="text-sm text-text-muted" data-id="element-88" data-mp="2qkuoargeqsej">ROI</span>
                  <span className={`text-2xl font-bold ${(view === '90day' ? organic90ROI : organic12ROI) >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-89" data-mp="2mmom7wqil0zy">
                    {(view === '90day' ? organic90ROI : organic12ROI).toFixed(0)}
                    %
                  </span>
                </div>
                {view === '12mo' && <div className="flex justify-between items-center" data-id="element-90" data-mp="22v9tnbaw66pf">
                    <span className="text-sm text-text-muted" data-id="element-91" data-mp="1f6h5i10ih35d">Net Profit</span>
                    <span className="text-xl font-bold text-green-400" data-id="element-92" data-mp="1b6zy321ixvxk">
                      ${organic12Profit.toLocaleString()}
                    </span>
                  </div>}
              </div>
              {view === '90day' && <p className="text-xs text-green-400/60 mt-4 italic" data-id="element-93" data-mp="15ut7dmhc5auz">
                  SEO is building your foundation — rankings haven't kicked in
                  yet. Patience pays off.
                </p>}
              {view === '12mo' && <p className="text-xs text-green-400/60 mt-4 italic" data-id="element-94" data-mp="1ju2meitt1yxd">
                  Month 8+ is where organic explodes. $0 per click means every
                  new visitor is pure profit.
                </p>}
            </div>

            {/* Paid Column */}
            <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-6" data-id="element-95" data-mp="1esvemjrnwzqp">
              <div className="flex items-center gap-2 mb-4" data-id="element-96" data-mp="3ukatm8vp4wrg">
                <Rocket size={18} className="text-orange-400" data-id="element-97" data-mp="3un5gs863gvok" />
                <h4 className="font-bold text-orange-400" data-id="element-98" data-mp="3fasg1h0gjly">Paid Ads</h4>
              </div>
              <div className="space-y-4" data-id="element-99" data-mp="26052isxrlv30">
                <div className="flex justify-between items-center" data-id="element-100" data-mp="262zmykgs3fwf">
                  <span className="text-sm text-text-muted" data-id="element-101" data-mp="212lpfwcszocn">Total Cost</span>
                  <span className="text-xl font-bold text-white" data-id="element-102" data-mp="1wallztxiydbb">
                    $
                    {(view === '90day' ? paid90Cost : paid12Cost).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-103" data-mp="1ib2odv2cakd9">
                  <span className="text-sm text-text-muted" data-id="element-104" data-mp="1idylxia9z716">
                    Revenue Generated
                  </span>
                  <span className="text-xl font-bold text-white" data-id="element-105" data-mp="18do9w0zz85nl">
                    $
                    {(view === '90day' ? paid90Rev : paid12Rev).toLocaleString()}
                  </span>
                </div>
                <div className="h-px bg-white/10" data-id="element-106" data-mp="3aaye840ww77g" />
                <div className="flex justify-between items-center" data-id="element-107" data-mp="3aa6srm113puw">
                  <span className="text-sm text-text-muted" data-id="element-108" data-mp="3f9jeg3g5yqvw">ROI</span>
                  <span className={`text-2xl font-bold ${(view === '90day' ? paid90ROI : paid12ROI) >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-109" data-mp="3f7ouosgmp4te">
                    {(view === '90day' ? paid90ROI : paid12ROI).toFixed(0)}%
                  </span>
                </div>
                {view === '12mo' && <div className="flex justify-between items-center" data-id="element-110" data-mp="2wc8hwqlx2bk5">
                    <span className="text-sm text-text-muted" data-id="element-111" data-mp="31ajpeuoqo9z7">Net Profit</span>
                    <span className="text-xl font-bold text-orange-400" data-id="element-112" data-mp="31cmpk7jlvjhn">
                      ${paid12Profit.toLocaleString()}
                    </span>
                  </div>}
              </div>
              {view === '90day' && <p className="text-xs text-orange-400/60 mt-4 italic" data-id="element-113" data-mp="li2uamou109v">
                  Ads deliver leads from week 1. But you're paying for every
                  single click.
                </p>}
              {view === '12mo' && <p className="text-xs text-orange-400/60 mt-4 italic" data-id="element-114" data-mp="t2n9sme9ait7">
                  Consistent revenue, but costs never drop. You pay per
                  impression forever.
                </p>}
            </div>
          </motion.div>

          {/* The punchline */}
          <motion.div key={view + '-insight'} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className={`p-4 rounded-xl text-center mb-10 ${view === '90day' ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-green-500/10 border border-green-500/20'}`} data-id="element-115" data-mp="120yy2hrnh4ih">
            {view === '90day' ? <p className="text-sm font-medium" data-id="element-116" data-mp="1gtsmwtstawzt">
                <span className="text-orange-400 font-bold" data-id="element-117" data-mp="20dhu1azkakzf">
                  At 90 days, paid wins on speed.
                </span>{' '}
                <span className="text-text-muted" data-id="element-118" data-mp="24f31ggtbu728">
                  But you've spent {Math.round(paid90Cost / organic90Cost)}x
                  more. Organic is still building — the real payoff is coming.
                </span>
              </p> : <p className="text-sm font-medium" data-id="element-119" data-mp="1gpihbulft3rd">
                <span className="text-green-400 font-bold" data-id="element-120" data-mp="1goqtx71zucc6">
                  At 12 months, organic delivers {Math.round(organic12ROI)}% ROI
                  vs {Math.round(paid12ROI)}% for paid.
                </span>{' '}
                <span className="text-text-muted" data-id="element-121" data-mp="1coghbbjp4noc">
                  That's ${organic12Profit.toLocaleString()} profit from organic
                  vs ${paid12Profit.toLocaleString()} from paid — and organic
                  keeps growing.
                </span>
              </p>}
          </motion.div>

          {/* Chart Visualization */}
          <div className="mb-2" data-id="element-122" data-mp="jn78kn0yo7qq">
            <div className="flex items-center justify-between mb-4" data-id="element-123" data-mp="jngwm52g44ts">
              <span className="text-xs text-text-dim uppercase tracking-wider" data-id="element-124" data-mp="etcovjlcvsrn">
                Monthly Revenue
              </span>
              <div className="flex items-center gap-4 text-xs" data-id="element-125" data-mp="4wrmz74muxs1">
                <span className="flex items-center gap-1.5" data-id="element-126" data-mp="4yb04ouzsspl">
                  <span className="w-3 h-3 rounded-sm bg-green-500/80" data-id="element-127" data-mp="12krxfu3q76bc"></span>
                  Organic
                </span>
                <span className="flex items-center gap-1.5" data-id="element-128" data-mp="xmqcf3mqowx8">
                  <span className="w-3 h-3 rounded-sm bg-orange-500/80" data-id="element-129" data-mp="slsyj38rip1j"></span>
                  Paid
                </span>
              </div>
            </div>
            <div className="h-48 flex items-end gap-1 px-1" data-id="element-130" data-mp="npc0nhbrgwb9">
              {Array.from({
              length: 12
            }).map((_, i) => {
              const oHeight = organicMonthlyRevenue[i] / maxRev * 100;
              const pHeight = paidMonthlyRevenue[i] / maxRev * 100;
              const isHighlighted = view === '90day' ? i < 3 : true;
              return <div key={i} className={`w-full flex gap-[2px] items-end h-full group relative transition-opacity duration-300 ${isHighlighted ? 'opacity-100' : 'opacity-30'}`} data-id="element-131" data-mp="1susgkq04sxfb" data-mpi={i}>
                    {/* Organic bar */}
                    <div className="w-1/2 bg-green-500/80 rounded-t-sm transition-all duration-500" style={{
                  height: `${Math.max(oHeight, 1)}%`
                }} data-id="element-132" data-mp="27ivk7sqpbx0y" />
                    {/* Paid bar */}
                    <div className="w-1/2 bg-orange-500/80 rounded-t-sm transition-all duration-500" style={{
                  height: `${Math.max(pHeight, 1)}%`
                }} data-id="element-133" data-mp="2lhv5qrix3vsw" />
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-black/90 text-[10px] p-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10 pointer-events-none border border-white/10" data-id="element-134" data-mp="2t1e7ug3usfrc">
                      <p className="font-bold text-white mb-1" data-id="element-135" data-mp="32ulavub4fjo9">Month {i + 1}</p>
                      <p className="text-green-400" data-id="element-136" data-mp="2xtxm7fad7ex7">
                        Organic: ${organicMonthlyRevenue[i].toLocaleString()}
                      </p>
                      <p className="text-orange-400" data-id="element-137" data-mp="2559w96eo81db">
                        Paid: ${paidMonthlyRevenue[i].toLocaleString()}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
            <div className="flex justify-between text-[10px] text-text-dim mt-2 px-1" data-id="element-138" data-mp="2f1mnlxmqu34w">
              {Array.from({
              length: 12
            }).map((_, i) => <span key={i} className="w-full text-center" data-id="element-139" data-mp="3qhbayq3wt468" data-mpi={i}>
                  {i + 1}
                </span>)}
            </div>
            <p className="text-center text-xs text-text-dim mt-1" data-id="element-140" data-mp="1yklaob1at9r">Month</p>
          </div>

          {/* Month 8 callout */}
          <div className="mt-6 flex items-center justify-center gap-2 text-sm" data-id="element-141" data-mp="3p1fmlf8ohdk6">
            <TrendingUp size={16} className="text-green-400" data-id="element-142" data-mp="3t2j5ybpxprq5" />
            <span className="text-text-muted" data-id="element-143" data-mp="3t2sv5x35nuus">
              Month 8: Organic revenue{' '}
              <span className="text-green-400 font-bold" data-id="element-144" data-mp="kn96zchcd2ml">surpasses</span> paid —
              and it's all profit from there.
            </span>
          </div>
        </div>

        <div className="mt-12 text-center max-w-3xl mx-auto mb-24" data-id="element-145" data-mp="bv76xfb6pocu">
          <p className="text-lg italic text-text-muted border-l-4 border-primary pl-6 text-left" data-id="element-146" data-mp="fvzgyarvrxa6">
            "Your organic foundation grows for free while paid campaigns
            accelerate results immediately.
            <span className="text-white font-bold not-italic" data-id="element-147" data-mp="18ctrhzt587xz">
              {' '}
              Paid traffic funds organic growth.
            </span>
            "
          </p>
        </div>

        {/* Retargeting Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24" data-id="element-148" data-mp="34v65wdce472h">
          <div className="flex flex-col justify-center" data-id="element-149" data-mp="2q84mo5oq76b8">
            <h3 className="text-3xl font-bold mb-4" data-id="element-150" data-mp="2q5idy2q3mkrf">
              Stop Wasting Money on Cold Traffic
            </h3>
            <p className="text-text-muted text-lg mb-8" data-id="element-151" data-mp="2h6wpxs1uydj9">
              The Retargeting Advantage: Spend pennies instead of dollars to
              ONLY retarget high-intent traffic.
            </p>
            <div className="bg-primary/10 border border-primary/30 p-6 rounded-xl" data-id="element-152" data-mp="278qp7drsfdfz">
              <div className="flex items-center mb-4" data-id="element-153" data-mp="27b3ekbzvkq32">
                <Target className="text-primary mr-3" size={32} data-id="element-154" data-mp="2c8v8tcnzsoaf" />
                <span className="text-2xl font-bold text-white" data-id="element-155" data-mp="5hlyql1lcix8">10x+ ROAS</span>
              </div>
              <p className="text-sm text-text-dim" data-id="element-156" data-mp="ag6xsh53wwk3">
                With retargeting campaigns vs cold traffic
              </p>
            </div>
          </div>

          <div className="bg-dark-card border border-white/10 rounded-2xl p-8 flex flex-col justify-center" data-id="element-157" data-mp="1uyrc0bqi7jyc">
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-white/5" data-id="element-158" data-mp="1pxbqkikezbff">
              <div className="text-left" data-id="element-159" data-mp="24qdp6dmejt3q">
                <p className="text-sm text-text-muted mb-1" data-id="element-160" data-mp="1zxav84dpqhn0">Cold Traffic CPC</p>
                <p className="text-3xl font-bold text-red-400" data-id="element-161" data-mp="2dophck2x2yya">$5.00</p>
              </div>
              <div className="text-right" data-id="element-162" data-mp="29ndycxejtbz2">
                <p className="text-sm text-text-muted mb-1" data-id="element-163" data-mp="29mm1zkd4na7q">Retargeting CPC</p>
                <p className="text-3xl font-bold text-green-400" data-id="element-164" data-mp="2nnf00f2n0nfl">$0.50</p>
              </div>
            </div>
            <div className="w-full bg-white/5 rounded-full h-4 overflow-hidden" data-id="element-165" data-mp="2ip3p010lw6vs">
              <div className="h-full bg-red-500 w-full relative" data-id="element-166" data-mp="t8m6cllbe6qa">
                <div className="absolute right-0 top-0 h-full bg-green-500 w-[10%]" data-id="element-167" data-mp="taywhm187nef"></div>
              </div>
            </div>
            <p className="text-center text-xs text-text-dim mt-2" data-id="element-168" data-mp="2octxnpeqkbya">
              Cost comparison visual
            </p>
          </div>
        </div>

        {/* Attribution Section */}
        <div className="bg-gradient-to-br from-dark-card to-blue-900/10 rounded-3xl p-8 md:p-12 border border-white/10" data-id="element-169" data-mp="2xcyu54ie5pxo">
          <div className="text-center mb-12" data-id="element-170" data-mp="32cl9bjhs5g94">
            <h3 className="text-3xl font-bold mb-4" data-id="element-171" data-mp="32dd4it7gtbyv">
              Attribution: Know Where Every Customer Comes From
            </h3>
            <p className="text-text-muted max-w-2xl mx-auto" data-id="element-172" data-mp="3c9zm994hvmwo">
              Most businesses don't track properly. You need to track everything
              and send that data back to your pixel to optimize for conversions.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-id="element-173" data-mp="3l21h8rvsoazk">
            {[{
            icon: MousePointerClick,
            label: 'CPC',
            desc: 'Cost Per Click'
          }, {
            icon: Target,
            label: 'CTR',
            desc: 'Click Through Rate'
          }, {
            icon: Users,
            label: 'CAC',
            desc: 'Customer Acq. Cost'
          }, {
            icon: DollarSign,
            label: 'LTV',
            desc: 'Lifetime Value'
          }, {
            icon: Eye,
            label: 'Pageviews',
            desc: 'Traffic Volume'
          }, {
            icon: Users,
            label: 'Leads',
            desc: 'Qualified Prospects'
          }, {
            icon: ShoppingCart,
            label: 'Conversions',
            desc: 'Sales/Bookings'
          }, {
            icon: BarChart3,
            label: 'ROAS',
            desc: 'Return on Ad Spend'
          }].map((metric, i) => <div key={i} className="bg-black/20 p-4 rounded-xl border border-white/5 flex flex-col items-center text-center hover:bg-white/5 transition-colors" data-id="element-174" data-mp="q7ss4gt97gcl" data-mpi={i}>
                <metric.icon className="text-primary mb-2" size={24} data-id="element-175" data-mp="h7con7cig97q" />
                <span className="font-bold text-white" data-id="element-176" data-mp="h97dgwqwklh3">{metric.label}</span>
                <span className="text-xs text-text-dim" data-id="element-177" data-mp="l8pydsiq4wjx">{metric.desc}</span>
              </div>)}
          </div>

          <div className="text-center" data-id="element-178" data-mp="190d7qwjxzoyg">
            <p className="text-xl md:text-2xl font-bold text-white leading-relaxed" data-id="element-179" data-mp="1dw27p0zvj7va">
              "Know <span className="text-primary" data-id="element-180" data-mp="106i9m8h8mysb">EXACTLY</span> where{' '}
              <span className="text-primary" data-id="element-181" data-mp="147aj92u0v5p4">EVERY</span> customer found you and
              throw more money in that direction."
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm text-text-muted" data-id="element-182" data-mp="1rw39cinyqjbt">
              <span className="px-3 py-1 bg-white/5 rounded-full border border-white/10" data-id="element-183" data-mp="1womuq1sw6ir6">
                Number pools for call tracking
              </span>
              <span className="px-3 py-1 bg-white/5 rounded-full border border-white/10" data-id="element-184" data-mp="3cexd5zqhtbv8">
                UTM parameters for CRM attribution
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}